import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { APP_CONFIG } from '@core-app/app.config';
import {
    ApiService,
    AuthService,
    DocumentService,
    ImageService,
    PageService,
    PageType,
    PostService,
    PostType,
    SettingsService,
    TranslateService,
    WindowService,
} from '@gallaghercp/core-frontend';
import { TranslateService as TranslateProvider } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-header',
    templateUrl: './header.component.html',
    standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
    protected auth = inject(AuthService);
    protected pages = inject(PageService);
    protected posts = inject(PostService);
    client = inject(ClientService);
    settings = inject(SettingsService);
    protected breakpoint = inject(BreakpointObserver);
    protected router = inject(Router);
    protected renderer = inject(Renderer2);
    protected api = inject(ApiService);
    protected imageService = inject(ImageService);
    protected windowService = inject(WindowService);
    protected documentService = inject(DocumentService);
    private platformId = inject(PLATFORM_ID);

    user: any;
    isLoggedIn = false;

    pages$?: Observable<any>;
    page?: PageType;
    post?: PostType;

    openChildNav: any;
    hamburger = false;
    hamburgerChildNav = false;
    authenticationType?: string;
    isMobileNav = false;
    isBrowser!: boolean;

    isTopNav$?: Observable<boolean>;
    isTransparentNav?: boolean;
    isTopNavTransparentSticky?: boolean = false;
    isIndividualMenuFlyout?: boolean = false;
    menuFlyoutPageId: number | undefined;

    subscriptions: Subscription[] = [];

    // Config variables
    darkMode?: boolean;
    postsInNavEnabled?: boolean;
    postsEnabled?: boolean;
    notificationsEnabled?: boolean;
    searchEnabled?: boolean;
    heroImagesEnabled?: boolean;
    isCollapsed = true;
    isDemoMode = false;

    isHeaderMinimised?: boolean;

    translateProvider: TranslateProvider = inject(TranslateProvider);

    logoUrl!: string;
    protected logoInvertedUrl!: string;
    protected logoTransparentUrl!: string;

    private translate: TranslateService = inject(TranslateService);

    ngOnInit(): void {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.isTopNav$ = this.settings.isTopNavigation();

        if (APP_CONFIG.demoMode !== undefined) {
            this.isDemoMode = APP_CONFIG.demoMode;
        }

        this.subscriptions.push(
            this.settings.showPosts().subscribe((state: any) => {
                this.postsEnabled = state;
            }),
        );

        this.subscriptions.push(
            this.settings.showNotifications().subscribe((state: any) => {
                this.notificationsEnabled = state;
            }),
        );

        this.subscriptions.push(
            this.settings.showSearch().subscribe((state: any) => {
                this.searchEnabled = state;
            }),
        );

        this.subscriptions.push(
            this.settings.showHeroImages().subscribe((state: any) => {
                this.heroImagesEnabled = state;
            }),
        );

        this.subscriptions.push(
            this.settings.showArticleThumbnails().subscribe((state: any) => {
                this.postsInNavEnabled = state;
            }),
        );

        this.subscriptions.push(
            this.settings.isDarkMode().subscribe((state: any) => {
                this.darkMode = state;
            }),
        );

        this.subscriptions.push(
            this.settings.isDarkModeEnabled().subscribe((state: any) => {
                if (state === false && this.darkMode) {
                    this.settings.toggle('isDarkMode');
                }
            }),
        );

        this.subscriptions.push(
            this.pages.currentPage$!.subscribe((page: PageType) => {
                this.page = page;
                this.post = undefined;
            }),
        );

        this.subscriptions.push(
            this.posts.currentPost$!.subscribe((post: PostType) => {
                this.post = post;
                this.page = undefined;
            }),
        );

        this.resetChildNav();
        this.hamburger = false;
        this.hamburgerChildNav = false;
        this.user = this.auth.getUser();
        this.isLoggedIn = this.auth.isAuthenticated();
        this.client.getAccessType().subscribe((type: any) => {
            this.authenticationType = type;
        });

        this.breakpoint.observe(['(max-width: 991px)']).subscribe(result => {
            this.isMobileNav = result.matches;
        });

        this.subscriptions.push(
            this.api.zoneChangeEmit.subscribe((zone: number | false) => {
                this.getPages(zone);
            }),
            this.translateProvider.onLangChange.subscribe(() => {
                this.getPages();
            }),
        );
    }

    showHeader(): boolean {
        return this.auth.isAuthenticated() || this.authenticationType === 'public';
    }

    trackByFn(idx: number, _item: any) {
        return idx;
    }

    childNav(page: any) {
        this.openChildNav = page;
    }

    resetChildNav() {
        this.openChildNav = false;
    }

    logout() {
        return this.auth.logout();
    }

    sortChildren(children: PageType[]) {
        children.sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    isCurrentPage(page: any) {
        return this.pages.getCurrentPage().url === page.url;
    }

    showInMenu(page: any, type: string) {
        if (type === 'no-child') {
            return page.meta.showInMenu == 'true' || page.meta.showInMenu == "'true'" || page.meta.showInMenu == true;
        } else if (type === 'has-child') {
            return (
                (page.meta.showInMenu == 'true' || page.meta.showInMenu == "'true'" || page.meta.showInMenu == true) &&
                !page.meta.hideNavDropdown
            );
        } else {
            return false;
        }
    }

    isDemoModeAndPostsLink(page: any) {
        return this.isDemoMode && page.url === 'articles';
    }

    getSiteName() {
        return this.client.getSiteName();
    }

    updateLogoUrl(): void {
        this.logoUrl = '/assets/img/site-logo.svg';
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    goToMain(elementId: string) {
        const element = this.documentService.getElementById(elementId);
        element?.focus();
    }

    /**
     * Retrieves a valid image URL based on the provided page metadata.
     *
     * @param {any} page - Page object used to determine the valid image URL.
     * @return {string} - A valid image URL as a string.
     */
    validImageUrl(page: any): SafeHtml {
        return this.imageService.getImageUrl(page, 'menu');
    }

    private getPages(zone?: number | false): void {
        const headers: any = zone ? { 'x-service-zone': String(zone) } : {};
        const currentPage: PageType = this.pages.getCurrentPage();

        this.pages$ = this.pages.getPages(headers).pipe(
            map((pages: PageType[]) => {
                return pages.map((page: PageType) => {
                    this.sortChildren(page.children);

                    // Open child navigation if conditions are met
                    if (
                        !this.openChildNav &&
                        currentPage &&
                        (currentPage?.parentId ?? 0) > 0 &&
                        page.id === currentPage.parentId
                    ) {
                        this.openChildNav = page;
                    }

                    // Translate common properties and assign them
                    const translatedProperties = ['name', 'description', 'url', 'tags'].reduce((acc, key) => {
                        acc[key] = this.translate.getPageSpecific(page, key);
                        return acc;
                    }, {} as any);

                    // Translate and map children
                    page.children = page.children.map((child: PageType) => ({
                        ...child,
                        name: this.translate.getPageSpecific(child, 'name'),
                        description: this.translate.getPageSpecific(child, 'description'),
                        url: this.translate.getPageSpecific(child, 'url'),
                        tags: this.translate.getPageSpecific(child, 'tags'),
                    }));

                    // Checks for true 'isFlyoutMenu' meta value on each page and sets a boolean and a page id to a couple of vars. This is used to allow individual flyout menus on pages if required.
                    if (page.meta && page.meta?.['isFlyoutMenu']) {
                        if (page.meta?.['isFlyoutMenu'] === 'true') {
                            this.isIndividualMenuFlyout = true;
                            this.menuFlyoutPageId = page.id;
                        }
                    }

                    return { ...page, ...translatedProperties };
                });
            }),
        );
    }
}
